<!-- 右侧信息：会员信息 + 最近浏览 + 交易订单 -->
<template>
  <el-container class="kefu">
    <el-header class="kefu-header" height="52px">
      <div
        :class="{ 'is-active': tabActivation('会员信息') }"
        class="kefu-header-item"
        @click="handleClick('会员信息')"
      >
        会员信息
      </div>
      <div
        :class="{ 'is-active': tabActivation('最近浏览') }"
        class="kefu-header-item"
        @click="handleClick('最近浏览')"
      >
        最近浏览
      </div>
      <div
        :class="{ 'is-active': tabActivation('交易订单') }"
        class="kefu-header-item"
        @click="handleClick('交易订单')"
      >
        交易订单
      </div>
    </el-header>
    <el-main class="kefu-content">
      <div v-if="hasConversation" v-loading="loading" class="kefu-panel">
        <el-alert
          v-if="!hasUserId"
          type="warning"
          :closable="false"
          show-icon
          class="mb-10px"
          title="该会话缺少会员 userId，右侧资料无法加载"
        />
        <!-- 基本信息 -->
        <UserBasicInfo
          v-if="activeTab === '会员信息' && hasUserId"
          :user="user"
          mode="kefu"
        >
          <template #header>
            <CardTitle title="基本信息" />
          </template>
        </UserBasicInfo>
        <!-- 账户信息 -->
        <el-card
          v-if="activeTab === '会员信息' && hasUserId"
          class="kefu-card mt-10px"
          shadow="never"
        >
          <template #header>
            <CardTitle title="账户信息" />
          </template>
          <UserAccountInfo :column="1" :user="user" :wallet="wallet" />
        </el-card>
      </div>
      <div v-show="hasConversation" class="kefu-scroll">
        <el-scrollbar ref="scrollbarRef" always @scroll="handleScroll">
          <!-- 最近浏览 -->
          <ProductBrowsingHistory v-if="activeTab === '最近浏览'" ref="productBrowsingHistoryRef" />
          <!-- 交易订单 -->
          <OrderBrowsingHistory v-if="activeTab === '交易订单'" ref="orderBrowsingHistoryRef" />
        </el-scrollbar>
      </div>
      <div v-show="!hasConversation" class="kefu-aside-empty">
        <p>选中会话后展示会员与订单</p>
      </div>
    </el-main>
  </el-container>
</template>

<script lang="ts" setup>
import ProductBrowsingHistory from './ProductBrowsingHistory.vue'
import OrderBrowsingHistory from './OrderBrowsingHistory.vue'
import { KeFuConversationRespVO } from '@/api/mall/promotion/kefu/conversation'
import { debounce } from 'lodash-es'
import { ElScrollbar as ElScrollbarType } from 'element-plus/es/components/scrollbar/index'
import { CardTitle } from '@/components/Card'
import UserBasicInfo from '@/views/member/user/detail/UserBasicInfo.vue'
import UserAccountInfo from '@/views/member/user/detail/UserAccountInfo.vue'
import * as UserApi from '@/api/member/user'
import * as WalletApi from '@/api/pay/wallet/balance'

defineOptions({ name: 'MemberBrowsingHistory' })

const activeTab = ref('会员信息')
const tabActivation = computed(() => (tab: string) => activeTab.value === tab)

/** tab 切换 */
const productBrowsingHistoryRef = ref<InstanceType<typeof ProductBrowsingHistory>>()
const orderBrowsingHistoryRef = ref<InstanceType<typeof OrderBrowsingHistory>>()
/** 已选中会话（有 id 即可切换 Tab；会员详情另需 userId） */
const hasConversation = computed(() => !!conversation.value?.id)
const hasUserId = computed(() => !!conversation.value?.userId)
const handleClick = async (tab: string) => {
  // 先切换高亮，避免「点了没反应」的体感；无会话时仅提示
  if (!hasConversation.value) {
    return
  }
  if (activeTab.value === tab) {
    return
  }
  activeTab.value = tab
  // 双 nextTick：确保 v-if 子组件挂载后再拉数据
  await nextTick()
  await nextTick()
  await getHistoryList()
}

/** 获得历史数据 */
const getHistoryList = async () => {
  if (!hasConversation.value) {
    return
  }
  switch (activeTab.value) {
    case '会员信息':
      if (!hasUserId.value) {
        return
      }
      await getUserData()
      await getUserWallet()
      break
    case '最近浏览':
      await productBrowsingHistoryRef.value?.getHistoryList(conversation.value)
      break
    case '交易订单':
      await orderBrowsingHistoryRef.value?.getHistoryList(conversation.value)
      break
    default:
      break
  }
}

/** 加载下一页数据 */
const loadMore = async () => {
  switch (activeTab.value) {
    case '会员信息':
      break
    case '最近浏览':
      await productBrowsingHistoryRef.value?.loadMore()
      break
    case '交易订单':
      await orderBrowsingHistoryRef.value?.loadMore()
      break
    default:
      break
  }
}

/** 浏览历史初始化 */
const conversation = ref<KeFuConversationRespVO>({} as KeFuConversationRespVO) // 用户会话
const initHistory = async (val: KeFuConversationRespVO) => {
  activeTab.value = '会员信息'
  conversation.value = val
  await nextTick()
  await getHistoryList()
}
defineExpose({ initHistory })

/** 处理消息列表滚动事件(debounce 限流) */
const scrollbarRef = ref<InstanceType<typeof ElScrollbarType>>()
const handleScroll = debounce(() => {
  const wrap = scrollbarRef.value?.wrapRef
  // 触底重置
  if (Math.abs(wrap!.scrollHeight - wrap!.clientHeight - wrap!.scrollTop) < 1) {
    loadMore()
  }
}, 200)

/** 查询用户钱包信息 */
const WALLET_INIT_DATA = {
  balance: 0,
  totalExpense: 0,
  totalRecharge: 0
} as WalletApi.WalletVO // 钱包初始化数据
const wallet = ref<WalletApi.WalletVO>(WALLET_INIT_DATA) // 钱包信息
const getUserWallet = async () => {
  if (!conversation.value.userId) {
    wallet.value = WALLET_INIT_DATA
    return
  }
  wallet.value =
    (await WalletApi.getWallet({ userId: conversation.value.userId })) || WALLET_INIT_DATA
}

/** 获得用户 */
const loading = ref(true) // 加载中
const user = ref<UserApi.UserVO>({} as UserApi.UserVO)
const getUserData = async () => {
  if (!conversation.value?.userId) {
    user.value = {} as UserApi.UserVO
    return
  }
  loading.value = true
  try {
    user.value = await UserApi.getUser(conversation.value.userId)
  } finally {
    loading.value = false
  }
}
</script>

<style lang="scss" scoped>
.kefu {
  width: 320px !important;
  height: 100%;
  overflow: hidden;
  background: var(--kefu-panel, #fff);
  border-left: 1px solid var(--kefu-border, #e8ecf1);

  &-header {
    display: flex;
    padding: 0 4px;
    background: var(--kefu-panel, #fff);
    border-bottom: 1px solid var(--kefu-border, #e8ecf1);
    align-items: stretch;
    justify-content: stretch;
  }

  &-header-item {
    position: relative;
    display: flex;
    flex: 1;
    height: 100%;
    font-size: 13px;
    color: var(--kefu-muted, #8a94a6);
    cursor: pointer;
    align-items: center;
    justify-content: center;
    transition: color 0.15s;

    &:hover {
      color: var(--kefu-text, #1f2937);
    }

    &.is-active {
      font-weight: 600;
      color: var(--kefu-accent, #1e3a5f);

      &::after {
        position: absolute;
        right: 12px;
        bottom: 0;
        left: 12px;
        height: 2px;
        background: var(--kefu-accent, #1e3a5f);
        content: '';
        border-radius: 2px 2px 0 0;
      }
    }
  }

  &-content {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    padding: 0 !important;
    margin: 0;
    overflow: hidden;
  }

  .kefu-panel {
    flex-shrink: 0;
    padding: 12px;
  }

  .kefu-scroll {
    flex: 1;
    min-height: 0;
    padding: 0 8px 12px;
  }

  .kefu-card {
    border: 1px solid var(--kefu-border, #e8ecf1);
    border-radius: 10px;

    :deep(.el-card__header) {
      padding: 10px 12px;
      border-bottom-color: var(--kefu-border, #e8ecf1);
    }

    :deep(.el-card__body) {
      padding: 10px 12px;
    }
  }

  .kefu-aside-empty {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    padding: 24px;
    text-align: center;

    p {
      margin: 0;
      font-size: 13px;
      line-height: 1.5;
      color: var(--kefu-muted, #8a94a6);
    }
  }
}
</style>
